import {getNumMipLevels} from "@cm/hal"
import {FetchArgs, ImageOpDataObjectPageSupplier} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object-page-supplier"
import {isPromise, TypedImageData} from "@cm/utils"
import {DataObjectImageDescriptor, TexturesApiService} from "@app/textures/service/textures-api.service"
import {DownloadFormat} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object"
import {assertNever} from "@cm/utils/misc"
import {ImageDataType} from "@generated"
import {generateMipLevel} from "@cm/browser-utils"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"

export class ImageOpDataObjectPageSupplierFullImage implements ImageOpDataObjectPageSupplier {
    constructor(
        private texturesApi: TexturesApiService,
        private descriptor: DataObjectImageDescriptor,
        private downloadFormat: Exclude<DownloadFormat, "tiles">,
    ) {
        this.imagePyramid = this.load()
    }

    // needsLoading(_mipLevelRegion: MipLevelRegion): boolean {
    //     return !this.imagePyramid || isPromise(this.imagePyramid)
    // }

    async fetch(args: FetchArgs) {
        if (isPromise(this.imagePyramid)) {
            this.imagePyramid = await this.imagePyramid
        }
        const mipLevel = args.mipLevelRegion.mipLevel ?? 0
        if (mipLevel < 0 || mipLevel >= this.imagePyramid.numMipLevels) {
            throw new Error(`Mip level ${mipLevel} out of bounds [0, ${this.imagePyramid.numMipLevels})`)
        }
        // const region = args.region ?? {
        //     x: 0,
        //     y: 0,
        //     ...getMipLevelSize(this.descriptor, mipLevel),
        // }
        const imageBitmap = this.imagePyramid.decodedImageByMipLevel[mipLevel]
        await args.target.writeImageData({
            sourceData: {
                data: imageBitmap,
                isSrgb: this.imagePyramid.isSrgb,
            },
            sourceRegion: args.mipLevelRegion,
            targetOffset: {x: args.mipLevelRegion.x, y: args.mipLevelRegion.y},
            targetMipLevel: mipLevel,
            maxPixelsPerUpload: TextureEditorSettings.MaxPixelsPerUpload,
        })
    }

    private async load(): Promise<ImagePyramid> {
        let url: string
        switch (this.downloadFormat) {
            case "exr":
                url = this.descriptor.downloadUrlExr
                break
            case "jpg":
                if (!this.descriptor.downloadUrlJpg) {
                    throw new Error("No JPG download URL for data object")
                }
                url = this.descriptor.downloadUrlJpg
                break
            default:
                assertNever(this.downloadFormat)
        }
        if (!url) {
            throw new Error("No download URL for data object")
        }
        const encodedData = await this.texturesApi.downloadRawData(url)
        const decodedImage = await this.decodeImage(encodedData, this.downloadFormat)
        const isSrgb = this.descriptor.imageDataType === ImageDataType.Color
        return this.generateImagePyramid(decodedImage, isSrgb)
    }

    private async generateImagePyramid(decodedImage: ImageBitmap, isSrgb: boolean): Promise<ImagePyramid> {
        const imagePyramid: ImagePyramid = {
            isSrgb,
            numMipLevels: getNumMipLevels(decodedImage),
            decodedImageByMipLevel: [decodedImage],
        }
        let mipLevelImageBitmap = decodedImage
        for (let mipLevel = 1; mipLevel < imagePyramid.numMipLevels; mipLevel++) {
            const nextMipLevelImageBitmap = await generateMipLevel(mipLevelImageBitmap)
            imagePyramid.decodedImageByMipLevel.push(nextMipLevelImageBitmap)
            mipLevelImageBitmap = nextMipLevelImageBitmap
        }
        return imagePyramid
    }

    private async decodeImage(encodedData: ArrayBuffer, format: Exclude<DownloadFormat, "tiles">): Promise<ImageBitmap> {
        switch (format) {
            case "exr":
                return this.decodeExrImage(encodedData)
            case "jpg":
                return this.decodeJpgImage(encodedData)
            default:
                assertNever(format)
        }
    }

    private async decodeJpgImage(encodedImageData: ArrayBuffer): Promise<ImageBitmap> {
        return createImageBitmap(new Blob([encodedImageData], {type: "image/jpeg"}))
    }

    private async decodeExrImage(encodedImageData: ArrayBuffer): Promise<ImageBitmap> {
        const decodedData = await this.texturesApi.decodeEXR(encodedImageData)
        return this.convertToImageData(decodedData)
    }

    private async convertToImageData(decodedData: TypedImageData): Promise<ImageBitmap> {
        //const convertedData = await this.ctx.texturesApi.convertImage(decodedData, "uint8", "RGBA") // this crashes with OOM for large images
        //const decodedUint8Data = new Uint8ClampedArray(convertedData.data.buffer, convertedData.data.byteOffset, convertedData.data.byteLength)
        if (decodedData.dataType !== "float32") {
            throw new Error(`Unsupported EXR data type: ${decodedData.dataType}`)
        }
        const sourceNumPixels = decodedData.width * decodedData.height
        const sourceNumChannels = decodedData.channelLayout.length
        const sourceData = new Float32Array(decodedData.data.buffer)
        const decodedUint8Data = new Uint8ClampedArray(sourceNumPixels * 4)
        if (sourceNumChannels === 1) {
            for (let i = 0; i < sourceNumPixels; i++) {
                const sourceIndex = i
                const destIndex = i * 4
                const value = sourceData[sourceIndex] * 255
                decodedUint8Data[destIndex] = value
                decodedUint8Data[destIndex + 1] = value
                decodedUint8Data[destIndex + 2] = value
                decodedUint8Data[destIndex + 3] = 255
            }
        } else if (sourceNumChannels === 3) {
            for (let i = 0; i < sourceNumPixels; i++) {
                const sourceIndex = i * sourceNumChannels
                const destIndex = i * 4
                decodedUint8Data[destIndex] = sourceData[sourceIndex] * 255
                decodedUint8Data[destIndex + 1] = sourceData[sourceIndex + 1] * 255
                decodedUint8Data[destIndex + 2] = sourceData[sourceIndex + 2] * 255
                decodedUint8Data[destIndex + 3] = 255
            }
        } else {
            throw new Error(`Unsupported EXR channel layout: ${decodedData.channelLayout}`)
        }
        const imageData = new ImageData(decodedUint8Data, decodedData.width, decodedData.height)
        return await createImageBitmap(imageData)
    }

    private imagePyramid: ImagePyramid | Promise<ImagePyramid>
}

type ImagePyramid = {
    isSrgb: boolean
    numMipLevels: number
    decodedImageByMipLevel: ImageBitmap[]
}
